/**
 * TrustGate Canonical Status System
 * 
 * Single source of truth for all Bound Intent status definitions
 * Used across console, public verification, and agent interfaces
 * 
 * NO AUTHORITY LOGIC - Pure UI state definitions
 */

/**
 * Canonical Intent Status Types
 * Ordered by typical lifecycle progression
 */
export type IntentStatus =
  | 'draft'
  | 'submitted'
  | 'awaiting_binding'
  | 'bound'
  | 'executed'
  | 'denied'
  | 'expired'
  | 'revoked'
  | 'superseded'
  | 'invalid';

/**
 * Status Metadata for UI Rendering
 */
export interface StatusMeta {
  label: string;
  tone: 'neutral' | 'info' | 'warn' | 'success' | 'danger' | 'system';
  icon: string;
  color: string;
  bgColor?: string;
  borderColor?: string;
}

/**
 * Canonical Status Metadata Map
 */
export const STATUS_META: Record<IntentStatus, StatusMeta> = {
  draft: {
    label: 'Draft Intent',
    tone: 'neutral',
    icon: '✏️',
    color: '#64748b',
    bgColor: 'bg-[#64748b]/20',
    borderColor: 'border-[#64748b]/50',
  },
  submitted: {
    label: 'Intent Submitted',
    tone: 'info',
    icon: '📤',
    color: '#3b82f6',
    bgColor: 'bg-[#3b82f6]/20',
    borderColor: 'border-[#3b82f6]/50',
  },
  awaiting_binding: {
    label: 'Awaiting Binding',
    tone: 'warn',
    icon: '⏳',
    color: '#f59e0b',
    bgColor: 'bg-[#f59e0b]/20',
    borderColor: 'border-[#f59e0b]/50',
  },
  bound: {
    label: 'Intent Bound',
    tone: 'success',
    icon: '🔒',
    color: '#10b981',
    bgColor: 'bg-[#10b981]/20',
    borderColor: 'border-[#10b981]/50',
  },
  executed: {
    label: 'Bound Intent Executed',
    tone: 'system',
    icon: '⚡',
    color: '#8b5cf6',
    bgColor: 'bg-[#8b5cf6]/20',
    borderColor: 'border-[#8b5cf6]/50',
  },
  denied: {
    label: 'Binding Denied',
    tone: 'danger',
    icon: '⛔',
    color: '#ef4444',
    bgColor: 'bg-[#ef4444]/20',
    borderColor: 'border-[#ef4444]/50',
  },
  expired: {
    label: 'Authority Expired',
    tone: 'warn',
    icon: '⏳',
    color: '#f59e0b',
    bgColor: 'bg-[#f59e0b]/20',
    borderColor: 'border-[#f59e0b]/50',
  },
  revoked: {
    label: 'Authority Revoked',
    tone: 'danger',
    icon: '⛔',
    color: '#ef4444',
    bgColor: 'bg-[#ef4444]/20',
    borderColor: 'border-[#ef4444]/50',
  },
  superseded: {
    label: 'Authority Superseded',
    tone: 'info',
    icon: '🔁',
    color: '#3b82f6',
    bgColor: 'bg-[#3b82f6]/20',
    borderColor: 'border-[#3b82f6]/50',
  },
  invalid: {
    label: 'Verification Failed',
    tone: 'danger',
    icon: '⚠️',
    color: '#1a1a2e',
    bgColor: 'bg-[#1a1a2e]/20',
    borderColor: 'border-[#1a1a2e]/50',
  },
};

/**
 * Legacy Status Mapping for Backwards Compatibility
 * Maps old status strings to canonical IntentStatus
 */
export function normalizeStatus(status: string): IntentStatus {
  const normalized = status.toUpperCase();
  
  switch (normalized) {
    case 'DRAFT':
      return 'draft';
    case 'SUBMITTED':
      return 'submitted';
    case 'PENDING':
      return 'awaiting_binding';
    case 'APPROVED':
    case 'ALLOW':
    case 'BOUND':
      return 'bound';
    case 'EXECUTED':
    case 'READY':
      return 'executed';
    case 'DENIED':
    case 'DENY':
      return 'denied';
    case 'EXPIRED':
      return 'expired';
    case 'REVOKED':
      return 'revoked';
    case 'SUPERSEDED':
      return 'superseded';
    case 'INVALID':
      return 'invalid';
    default:
      return 'submitted'; // Fail-safe default
  }
}

/**
 * Get Status Metadata
 */
export function getStatusMeta(status: string | IntentStatus): StatusMeta {
  const normalized = typeof status === 'string' ? normalizeStatus(status) : status;
  return STATUS_META[normalized] || STATUS_META.submitted;
}

/**
 * Status Utilities
 */
export const StatusUtils = {
  /**
   * Check if status represents a terminal state
   */
  isTerminal(status: IntentStatus): boolean {
    return ['executed', 'denied', 'expired', 'revoked', 'invalid'].includes(status);
  },

  /**
   * Check if status represents an active binding
   */
  isBound(status: IntentStatus): boolean {
    return ['bound', 'executed'].includes(status);
  },

  /**
   * Check if status represents a pending state
   */
  isPending(status: IntentStatus): boolean {
    return ['submitted', 'awaiting_binding'].includes(status);
  },

  /**
   * Get CSS color class for text
   */
  getTextColorClass(status: IntentStatus): string {
    return `text-[${STATUS_META[status].color}]`;
  },

  /**
   * Get full Tailwind classes for badge rendering
   */
  getBadgeClasses(status: IntentStatus): string {
    const meta = STATUS_META[status];
    return `${meta.bgColor} ${meta.borderColor} text-[${meta.color}]`;
  },
};
